import * as React from "react";
import * as autosize from "autosize";
import "./textArea.scss";
import {isNullOrUndefined} from "util";
import {isPhoneScreen} from "../../../../utils/Utils";
import classNames = require("classnames");

interface IProps {

	/**
	 * Textarea value.
	 */
	value?: string;
	/**
	 * Textarea placeholder.
	 */
	placeholder?: string;
	/**
	 * Number of textarea rows (height).
	 */
	rows?: number;
	/**
	 * Number of textarea columns (width).
	 */
	cols?: number;
	/**
	 * Disable flag.
	 */
	disabled?: boolean;
	/**
	 * Readonly flag.
	 */
	readonly?: boolean;
	/**
	 * If text area should auto resize when typing.
	 */
	autoSize?: boolean;
	/**
	 * Textarea focus flag.
	 */
	focus?: boolean;
	/**
	 * Indicates if textarea should have border.
	 */
	hasBorder?: boolean;
	/**
	 * Textarea on change callback.
	 */
	onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
	/**
	 * On click callback.
	 * @param {React.MouseEvent<HTMLTextAreaElement>} event
	 */
	onClick?: (event: React.MouseEvent<HTMLTextAreaElement>) => void;
	/**
	 * On focus callback.
	 * @param {React.FocusEvent<HTMLTextAreaElement>} event
	 */
	onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
}

export default class TextArea extends React.Component<IProps, {}> {

	public static defaultProps: Partial<IProps> = {
		rows: 1
	};

	textArea: HTMLTextAreaElement;

	constructor(props: IProps) {
		super(props);
		this.onChange = this.onChange.bind(this);
		this.onClick = this.onClick.bind(this);
		this.onFocus = this.onFocus.bind(this);
	}

	componentDidUpdate(prevProps: IProps) {
		// On a phone any re-render would pull the keyboard back up, so only a newly given focus counts there
		if (this.props.focus && (!isPhoneScreen() || !prevProps.focus)) {
			this.textArea.focus();
		}
		autosize.update(this.textArea);
	}

	componentDidMount(): void {
		if (this.props.focus && !isPhoneScreen()) {
			this.textArea.focus();
		}
		if (this.props.autoSize && this.textArea) {
			autosize(this.textArea);
		}
	}

	render() {

		const styleFromProps = this.props.disabled ? {cursor: "not-allowed"} : {};
		const classFromProps = classNames({
			"a-text-area": true,
			"has-border": this.props.hasBorder,
			"is-disabled": this.props.disabled,
			"is-readonly": this.props.readonly
		});

		return (
			<textarea ref={(t) => this.textArea = t}
					  className={classFromProps}
					  style={styleFromProps}
					  value={this.props.value}
					  rows={this.props.rows}
					  cols={this.props.cols}
					  readOnly={this.props.readonly}
					  disabled={this.props.disabled}
					  placeholder={this.props.placeholder}
					  onFocus={this.onFocus}
					  onClick={this.onClick}
					  onChange={this.onChange}/>
		);
	}

	/**
	 * On input change.
	 * @param {ChangeEvent<HTMLInputElement>} event Input event.
	 */
	private onChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
		if ((!this.props.disabled || this.props.readonly) && !isNullOrUndefined(this.props.onChange)) {
			this.props.onChange(event);
		}
	}

	/**
	 * On click callback.
	 * @param {React.MouseEvent<HTMLTextAreaElement>} event
	 */
	private onClick(event: React.MouseEvent<HTMLTextAreaElement>) {
		if (!isNullOrUndefined(this.props.onClick)) {
			this.props.onClick(event);
		}
	}

	/**
	 * On focus callback.
	 * @param {React.FocusEvent<HTMLTextAreaElement>} event
	 */
	private onFocus(event: React.FocusEvent<HTMLTextAreaElement>) {
		if (!isNullOrUndefined(this.props.onFocus)) {
			this.props.onFocus(event);
		}
	}
}
